/*
 * A single-line text input, wired to whatever Field it finds itself in.
 *
 * The component is thin on purpose. It is a native `<input>` with the shared
 * shell and the Field's ids adopted; everything else is the platform's. A
 * hand-built text field would have to reimplement selection, autofill, the
 * password manager hooks, dictation and the mobile keyboard hints, and the last
 * two are exactly what the people using this console on a laptop in a field
 * office need most.
 */

import type { InputHTMLAttributes, Ref } from 'react'
import { joinIds } from '../aria'
import { useFieldControl } from '../Field/fieldContext'
import styles from './TextInput.module.css'

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Right-aligns and applies tabular figures. For counts, amounts and codes. */
  numeric?: boolean
  /**
   * The underlying input, for the one thing a form cannot do without it:
   * putting the cursor in the field it has just rejected.
   *
   * Declared explicitly even though React 19 passes `ref` to a function
   * component as an ordinary prop, because `InputHTMLAttributes` does not carry
   * it and the call site would otherwise be a type error at exactly the moment
   * somebody is trying to do the accessible thing. `forwardRef` is not used:
   * it is deprecated in this version and adds a wrapper component that shows up
   * in every stack trace through this input.
   */
  ref?: Ref<HTMLInputElement>
}

export function TextInput({ numeric = false, className, ref, ...rest }: TextInputProps) {
  const field = useFieldControl()

  const inputClassName = [styles.input, numeric ? styles.numeric : '', className]
    .filter(Boolean)
    .join(' ')

  return (
    <input
      type="text"
      {...rest}
      ref={ref}
      id={rest.id ?? field?.id}
      className={inputClassName}
      aria-describedby={joinIds(rest['aria-describedby'], field?.describedBy)}
      aria-invalid={rest['aria-invalid'] ?? (field?.invalid ? true : undefined)}
      aria-required={rest['aria-required'] ?? (field?.required ? true : undefined)}
    />
  )
}
